import { useState, useEffect, useCallback } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import {
  getFirestore,
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  Timestamp
} from 'firebase/firestore'
import Database from '../../../types/Database'

type TicketStatus = Database.SupportTicketStatus

const STATUS_COLORS: Record<
  TicketStatus,
  'warning' | 'info' | 'success' | 'default'
> = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success',
  closed: 'default'
}

interface TicketRow {
  id: string
  userId: string
  userEmail: string
  userName: string
  subject: string
  message: string
  status: TicketStatus
  createdAt: Date
  resolvedAt: Date | null
}

const SupportTicketsTab = () => {
  const [tickets, setTickets] = useState<TicketRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Resolve dialog state
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState<TicketRow | null>(null)
  const [resolveComment, setResolveComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  const fetchTickets = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const db = getFirestore()
      const ticketsRef = collection(db, 'supportTickets')
      const q = query(ticketsRef, orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      const userCache = new Map<
        string,
        { email: string; displayName: string }
      >()

      const resolveUser = async (
        uid: string
      ): Promise<{ email: string; displayName: string }> => {
        if (userCache.has(uid)) return userCache.get(uid)!
        try {
          const userDoc = await getDoc(doc(db, 'users', uid))
          if (userDoc.exists()) {
            const data = userDoc.data()
            const result = {
              email: (data.email as string) || '—',
              displayName: (data.displayName as string) || uid
            }
            userCache.set(uid, result)
            return result
          }
        } catch {
          // fallback
        }
        const fallback = { email: '—', displayName: uid }
        userCache.set(uid, fallback)
        return fallback
      }

      const rows: TicketRow[] = await Promise.all(
        snapshot.docs.map(async d => {
          const data = d.data()
          const user = await resolveUser(data.userId as string)
          return {
            id: d.id,
            userId: data.userId as string,
            userEmail: user.email,
            userName: user.displayName,
            subject: data.subject as string,
            message: data.message as string,
            status: data.status as TicketStatus,
            createdAt: data.createdAt?.toDate?.() ?? new Date(),
            resolvedAt: data.resolvedAt?.toDate?.() ?? null
          }
        })
      )

      setTickets(rows)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to load support tickets'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])

  const handleOpenResolve = (ticket: TicketRow) => {
    setSelectedTicket(ticket)
    setResolveComment('')
    setResolveDialogOpen(true)
  }

  const handleCloseResolve = () => {
    setResolveDialogOpen(false)
    setSelectedTicket(null)
    setResolveComment('')
  }

  const handleResolve = async () => {
    if (!selectedTicket) return
    if (resolveComment.length < 1 || resolveComment.length > 1000) return

    setSubmitting(true)
    try {
      const db = getFirestore()
      const ticketRef = doc(db, 'supportTickets', selectedTicket.id)
      await updateDoc(ticketRef, {
        status: 'resolved' as TicketStatus,
        resolvedAt: Timestamp.now(),
        resolutionComment: resolveComment.trim()
      })

      setSnackbar({
        open: true,
        message: 'Support ticket marked as resolved.',
        severity: 'success'
      })
      handleCloseResolve()
      await fetchTickets()
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to resolve ticket'
      setSnackbar({ open: true, message, severity: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box display='flex' justifyContent='center' py={6}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant='outlined' onClick={fetchTickets}>
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant='h6' sx={{ mb: 2 }}>
        Support Tickets
      </Typography>

      {tickets.length === 0 ? (
        <Typography color='text.secondary'>
          No support tickets found.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map(ticket => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    {ticket.createdAt.toLocaleDateString()}
                  </TableCell>
                  <TableCell>{ticket.userName}</TableCell>
                  <TableCell>{ticket.userEmail}</TableCell>
                  <TableCell>{ticket.subject}</TableCell>
                  <TableCell>
                    <Typography
                      variant='body2'
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {ticket.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status.replace('_', ' ')}
                      color={STATUS_COLORS[ticket.status] ?? 'default'}
                      size='small'
                    />
                  </TableCell>
                  <TableCell>
                    {ticket.status !== 'resolved' &&
                    ticket.status !== 'closed' ? (
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => handleOpenResolve(ticket)}
                      >
                        Resolve
                      </Button>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        {ticket.resolvedAt
                          ? `Resolved ${ticket.resolvedAt.toLocaleDateString()}`
                          : 'Resolved'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={handleCloseResolve}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Resolve Support Ticket</DialogTitle>
        <DialogContent>
          {selectedTicket && (
            <Box sx={{ mb: 2 }}>
              <Typography variant='body2' color='text.secondary'>
                <strong>User:</strong> {selectedTicket.userName} (
                {selectedTicket.userEmail})
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                <strong>Subject:</strong> {selectedTicket.subject}
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                <strong>Message:</strong> {selectedTicket.message}
              </Typography>
            </Box>
          )}

          <TextField
            label='Resolution Comment'
            multiline
            rows={4}
            fullWidth
            required
            value={resolveComment}
            onChange={e => setResolveComment(e.target.value)}
            error={resolveComment.length > 1000}
            helperText={`${resolveComment.length}/1000 characters (minimum 1)`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResolve} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleResolve}
            variant='contained'
            disabled={
              submitting ||
              resolveComment.length < 1 ||
              resolveComment.length > 1000
            }
          >
            {submitting ? <CircularProgress size={20} /> : 'Mark as Resolved'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default SupportTicketsTab
